<script lang="ts" setup>
import type { IntegrationLoginProvider, SocialLoginProvider } from '~kui/types/auth';
import type { ResponseResult } from '~kui/types/formResult';

import { Heading, Text } from '~ds/components/ui/typography';
import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import KUIUnifiedAuthSocialButtons from '~kui/components/KUI/Unified/Auth/KUIUnifiedAuthSocialButtons.vue';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

interface Props {
  loading: boolean;
  initialEmail?: string;
  emailLocked?: boolean;
  integrationProviders?: string[];
  showSocial?: boolean;
  helpText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  initialEmail: '',
  emailLocked: false,
  integrationProviders: () => [],
  showSocial: true,
  helpText: '',
});

const emit = defineEmits<{
  submit: [email: string];
  socialLogin: [provider: SocialLoginProvider | IntegrationLoginProvider];
}>();

const { t } = useI18n()
const formResult = ref<ResponseResult | null>(null);

const formConfig = defineFormConfig(
  {
    email: defineField({
      type: 'text',
      options: {
        label: t('general.formFields.email.label'),
        hideLabel: true,
        type: 'email',
        autocomplete: 'email',
        placeholder: t('general.formFields.email.placeholder'),
        required: true,
        disabled: props.emailLocked,
        dataTestId: 'auth-email-field',
      },
      initialValue: props.initialEmail,
      schema: z.email(t('general.formFields.email.validation')),
    }),
  },
  t('general.auth.submitButton.getStarted'),
);

const handleSubmit = (value: z.infer<typeof formConfig.schema>) => {
  formResult.value = null;
  emit('submit', value.email);
};

const handleSocialLogin = (provider: SocialLoginProvider | IntegrationLoginProvider) => {
  emit('socialLogin', provider);
};

const setError = (message: string) => {
  formResult.value = { success: false, message };
};

defineExpose({ setError });
</script>

<template>
  <div class="flex flex-col items-center">
    <Heading
      size="xl"
      as="h1"
      class="text-center"
      :class="helpText ? 'mb-3' : 'mb-8'"
    >
      {{ t('general.auth.getStarted') }}
    </Heading>
    <Text
      v-if="helpText"
      size="lg"
      class="text-center mb-8"
      data-testid="auth-email-help-text"
    >
      {{ helpText }}
    </Text>

    <KUICoreForm
      class="w-full"
      :form-config="formConfig"
      :form-result="formResult"
      :loading="loading"
      :allow-clean-submit="emailLocked"
      submit-button-size="lg"
      @submit="handleSubmit"
    />

    <KUIUnifiedAuthSocialButtons
      v-if="showSocial && !emailLocked"
      :disabled="loading"
      :integration-providers="integrationProviders"
      @social-login="handleSocialLogin"
    />
  </div>
</template>
